<script setup lang="ts">
import { format, isSameDay, isToday } from 'date-fns'

import { CalendarIcon } from '@radix-icons/vue'
import { CircleX } from 'lucide-vue-next'
import type { DatePickerRangeObject } from 'v-calendar/dist/types/src/use/datePicker.js'
import { computed, ref, watch } from 'vue'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import Calendar from '../ui/calendar/Calendar.vue'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'

type CalendarText = {
  dates: string
  daysString: string
  class?: string
}

const props = defineProps<{
  class?: any
  startDate?: Date
  endDate?: Date
}>()

const today = new Date()

const startDate = ref<Date | undefined>(props.startDate)
const endDate = ref<Date | undefined>(props.endDate)

const emits = defineEmits<{
  (e: 'update:change', payload: Partial<DatePickerRangeObject>): void
}>()

watch([startDate, endDate], ([newStart, newEnd]) => {
  emits('update:change', { start: newStart, end: newEnd })
})

const calendarText = computed(() =>
  getCalendarRangeText({ start: startDate.value, end: endDate.value })
)

function getCalendarText(date: Date | undefined): string {
  if (!date) return 'Pick a date'

  const isDateToday = isToday(date)
  if (isDateToday) return 'Today'

  return format(date, 'LLL dd, y')
}

function getCalendarRangeText(dateRange: {
  start: Date | undefined
  end: Date | undefined
}): CalendarText {
  const calendarText: CalendarText = {
    dates: '',
    daysString: '',
  }

  if (!dateRange.start && !dateRange.end) {
    calendarText.dates = 'Pick a dateRange'
    return calendarText
  }

  const startTimeIsToday = dateRange.start ? isToday(dateRange.start) : false
  const endTimeIsToday = dateRange.end ? isToday(dateRange.end) : false
  const dateRangeIsSameDay =
    dateRange.start && dateRange.end ? isSameDay(dateRange.start, dateRange.end) : false

  if (startTimeIsToday && endTimeIsToday) {
    calendarText.dates = 'Today'
    return calendarText
  }

  if (dateRangeIsSameDay) {
    calendarText.dates = format(dateRange.start, 'LLL dd, y')
    return calendarText
  }

  if (dateRange.start) {
    if (startTimeIsToday) {
      calendarText.dates += 'Today - '
    } else {
      calendarText.dates += format(dateRange.start, 'LLL dd, y') + ' - '
    }
  }

  if (dateRange.end) {
    if (endTimeIsToday) {
      calendarText.dates += 'Today'
    } else {
      calendarText.dates += format(dateRange.end, 'LLL dd, y')
    }
  }

  if (dateRange.start && dateRange.end && !dateRangeIsSameDay) {
    const days = Math.round(
      (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
    )

    calendarText.daysString = ` ${endTimeIsToday ? 'Previous ' : ''}${days > 1 || !dateRangeIsSameDay ? days : ''} day${days > 1 ? 's' : ''}`
  }

  return calendarText
}
</script>
<template>
  <div :class="cn('w-full flex items-center gap-1 flex-wrap shrink', props.class)">
    <div class="flex grow items-center bg-slate-50 border-slate-200 border rounded-md pl-2">
      <span class="shrink-0 font-semibold text-xs pr-2">From</span>
      <Popover>
        <PopoverTrigger as-child>
          <Button
            id="date"
            :variant="'outline'"
            :class="
              cn(
                'w-full max-w-[400px] justify-start text-left font-normal bg-white',
                !startDate && 'text-muted-foreground'
              )
            "
          >
            <CalendarIcon class="mr-2 h-4 w-4" />

            <div class="flex justify-between w-full">
              <span>
                {{ getCalendarText(startDate) }}
              </span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-auto p-0" align="start" :avoid-collisions="true">
          <Calendar v-model="startDate" />
        </PopoverContent>
      </Popover>
      <Button
        v-if="startDate"
        @click="startDate = undefined"
        variant="ghost"
        class="shrink-0 p-0 w-8 h-9 rounded-md hover:bg-slate-200"
      >
        <CircleX class="w-4 h-4" />
      </Button>
    </div>
    <div class="flex grow items-center bg-slate-50 border-slate-200 border rounded-md pl-2">
      <span class="shrink-0 font-semibold text-xs pr-2">To</span>
      <Popover>
        <PopoverTrigger as-child>
          <Button
            id="date"
            :variant="'outline'"
            :class="
              cn(
                'w-full max-w-[400px] justify-start text-left font-normal bg-white',
                !endDate && 'text-muted-foreground'
              )
            "
          >
            <CalendarIcon class="mr-2 h-4 w-4" />

            <div class="flex justify-between w-full">
              <span>
                {{ getCalendarText(endDate) }}
              </span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-auto p-0" align="start" :avoid-collisions="true">
          <Calendar v-model="endDate" />
        </PopoverContent>
      </Popover>
      <Button
        v-if="endDate && !isToday(endDate)"
        @click="endDate = undefined"
        variant="ghost"
        class="shrink-0 p-0 w-8 h-9 rounded-md hover:bg-slate-200"
      >
        <CircleX class="w-4 h-4" />
      </Button>
    </div>
    <span v-if="calendarText.daysString" class="text-muted-foreground px-1">{{
      calendarText.daysString
    }}</span>
  </div>
</template>
